// Updated DashboardPage.js
// Automatically deletes old PreQual PDFs from Firebase Storage when user clicks "Edit Answers"

import { AmortizationCalculator } from "./formUtils";
import React, { useState, useEffect, useRef } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, storage } from "./firebase";
import { listAll, ref, deleteObject } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import "./DashboardPage.css";
import LanguageToggle from "./LanguageToggle";
import {
  FaWhatsapp,
  FaPhone,
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaTiktok,
} from "react-icons/fa";

export default function DashboardPage() {
  const [clientInfo, setClientInfo] = useState(null);
  const [docId, setDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState("Purchase");
  const [hasCompleted, setHasCompleted] = useState(false);
  const [pdfLinks, setPdfLinks] = useState({});
  const [officer, setOfficer] = useState(null);
  const navigate = useNavigate();

  function hasSpouseData(formData) {
    if (!formData || formData.prenuptialAgreement === "Yes") return false;
    const fields = [
      "spouseFirstName",
      "spouseLastName",
      "spousePhone",
      "spouseEmail",
      "spouseIncome",
      "spouseEmploymentType",
      // add more spouse fields if you ever display them
    ];
    return fields.some((key) => !!formData[key]);
  }

  function checkCompletion({ transactionType, currentStep }) {
    const completionSteps = {
      Purchase: 20,
      Refinance: 16,
    };
    return currentStep >= (completionSteps[transactionType] || 20);
  }

  useEffect(() => {
    const loggedIn = localStorage.getItem("isLoggedIn");
    const storedId = localStorage.getItem("clientDocId");
    if (!loggedIn || !storedId) {
      window.location.href = "/en/login";
      return;
    }
    setDocId(storedId);
    loadClientData(storedId);
  }, []);

  const loadClientData = async (id) => {
    try {
      const ref = doc(db, "clients", id);
      const snapshot = await getDoc(ref);
      if (snapshot.exists()) {
        const data = snapshot.data();
        setClientInfo(data);
        if (!data.formDataPurchase && data.formDataRefinance) {
          setSelectedView("Refinance");
        }

        setHasCompleted(checkCompletion(data));

        // Store both purchase and refinance PDF links
        setPdfLinks({
          purchase: data.preQualPDFs || {},
          refinance: data.refinancePreQualPDFs || {},
        });
      }
    } catch (err) {
      console.error("Failed to load client data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchOfficer = async () => {
      const officerId = clientInfo?.officerId || clientInfo?.assignedOfficer;
      if (officerId) {
        try {
          const ref = doc(db, "officers", officerId);
          const snapshot = await getDoc(ref);
          if (snapshot.exists()) {
            setOfficer(snapshot.data());
          }
        } catch (err) {
          console.error("Error fetching officer:", err);
        }
      }
    };
    fetchOfficer();
  }, [clientInfo]);

  const [showCalculator, setShowCalculator] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/en/login";
  };

  const handleEditAnswers = async () => {
    try {
      if (!docId) return;

      const prequalPath = `clients/${docId}/prequals/`;
      const folderRef = ref(storage, prequalPath);
      const listResult = await listAll(folderRef);
      await Promise.all(listResult.items.map((item) => deleteObject(item)));

      const fieldKey =
        selectedView === "Refinance" ? "formDataRefinance" : "formDataPurchase";

      // Clear main & spouse income fields on edit
      const clearedFields = {
        ...clientInfo[fieldKey],
        income: "",
        incomeFrequency: "",
        hoursPerWeek: "",
        spouseIncome: "",
        spouseIncomeFrequency: "",
        spouseHoursPerWeek: "",
        completed: false,
      };

      await updateDoc(doc(db, "clients", docId), {
        preQualPDFs: {},
        [fieldKey]: clearedFields,
      });

      navigate(
        selectedView === "Refinance"
          ? `/en/refinance?id=${docId}`
          : `/en/questions?id=${docId}`
      );
    } catch (err) {
      console.error("Failed to delete old PreQual PDFs:", err);
      alert("Could not reset previous prequalification documents.");
    }
  };

  if (loading || !clientInfo)
    return <div style={{ padding: "2rem" }}>Loading dashboard...</div>;

  const purchaseApproved = clientInfo.purchasePreQualApproved;
  const refinanceApproved = clientInfo.refinancePreQualApproved;

  const cardStyle = {
    background: "#f8f8f8",
    padding: "10px",
    borderRadius: "6px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  };

  const formatCurrency = (value) => {
    if (!value) return "$0.00";
    const num = parseFloat(value.toString().replace(/[^0-9.]/g, ""));
    return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  const formatPhone = (phone) => {
    if (!phone) return "N/A";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length !== 10) return phone;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  };

  const pdfButtonStyle = {
    backgroundColor: "#0096b2",
    color: "#fff",
    fontSize: "16px",
    border: "none",
    padding: "10px 20px",
    marginRight: "10px",
    marginBottom: "10px",
    borderRadius: "8px",
    cursor: "pointer",
  };

  const circleButtonStyle = {
    backgroundColor: "#0096b2",
    color: "#fff",
    fontSize: "18px",
    border: "none",
    borderRadius: "50%",
    width: "100px",
    height: "100px",
    cursor: "pointer",
  };
  const toggleButtonStyle = {
    backgroundColor: "#0096b2",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: "8px",
    marginRight: "10px",
    cursor: "pointer",
  };

  const purchaseStarted = !!clientInfo.formDataPurchase;
  const refinanceStarted = !!clientInfo.formDataRefinance;

  const purchaseComplete = clientInfo?.formDataPurchase?.completed === true;
  const refinanceComplete = clientInfo?.formDataRefinance?.completed === true;

  const formData =
    selectedView === "Refinance"
      ? clientInfo.formDataRefinance
      : clientInfo.formDataPurchase;

  const getClientName = () => {
    return clientInfo?.firstName && clientInfo?.lastName
      ? `${clientInfo.firstName} ${clientInfo.lastName}`
      : clientInfo?.formDataPurchase?.firstName &&
        clientInfo?.formDataPurchase?.lastName
      ? `${clientInfo.formDataPurchase.firstName} ${clientInfo.formDataPurchase.lastName}`
      : clientInfo?.formDataRefinance?.firstName &&
        clientInfo?.formDataRefinance?.lastName
      ? `${clientInfo.formDataRefinance.firstName} ${clientInfo.formDataRefinance.lastName}`
      : "Client";
  };

  const handleRefresh = () => {
    if (docId) loadClientData(docId);
  };

  const approvalStatus =
    clientInfo.officerApproved === true
      ? "approved"
      : clientInfo.officerApproved === false
      ? "rejected"
      : "pending";

  const officerNotes = clientInfo.officerNotes || "";

  return (
    <main
      className="dash-main-container"
      style={{
        position: "relative",
        maxWidth: "700px",
        margin: "0 auto",
        background: "#fff",
        fontFamily: "Arial, sans-serif",
        padding: "2rem",
        borderRadius: "16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        textAlign: "center",
      }}
    >
      <div className="dash-inner-container">
        <div
          className="dashboard-logo-row"
          style={{ position: "absolute", top: 48, right: 24, zIndex: 2 }}
        >
          <div style={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
            <LanguageToggle />
          </div>
          <button
            onClick={handleRefresh}
            className="dashboard-refresh-btn"
            title="Refresh"
            aria-label="Refresh"
          >
            &#x21bb;
          </button>

          <a href="/" className="dashboard-logo-link">
            <img
              src="/assets/HMBHOUSE.png"
              alt="Home Mortgage Logo"
              className="dashboard-logo-img"
            />
          </a>
        </div>
      </div>

      <h1
        className="dash-welcome-header"
        style={{ color: "#0096b2", textAlign: "left" }}
      >
        Welcome, {getClientName()}
      </h1>

      {/* Status section - always on the top right */}

      <div className="dash-status-section">
        <span className="status-label">Status:</span>
        <span className={`status-badge status-${approvalStatus}`}>
          {approvalStatus === "approved" && "✅ Reviewed"}
          {approvalStatus === "pending" && "⏳ Pending"}
          {approvalStatus === "rejected" && "📄 In Review"}
        </span>
      </div>

      <div className="dash-officer-container">
        <h3
          style={{
            color: "#007b94",
            marginBottom: "0.75rem",
            borderBottom: "1px solid #ccc",
            paddingBottom: "0.25rem",
            textAlign: "left",
          }}
        >
          Assigned Loan Officer:
        </h3>

        {officer ? (
          <div className="dash-officer-row">
            {/* Officer Info (left/above on mobile) */}
            <div className="dash-officer-info">
              <p className="officer-name" style={{ marginBottom: 0 }}>
                <strong>{officer.name}</strong>
              </p>
              {officer.mloNumber && (
                <div
                  style={{
                    fontSize: "0.98em",
                    color: "#222",
                    fontWeight: 600,
                    marginTop: 0,
                    marginBottom: "0.2em",
                    textAlign: "left",
                  }}
                >
                  {officer.mloNumber}
                </div>
              )}

              <div className="officer-contact-label">Contact:</div>
              <div className="officer-contact-btns">
                {officer.phone && (
                  <a
                    href={`https://wa.me/${officer.phone.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-btn whatsapp"
                    title="WhatsApp"
                  >
                    <FaWhatsapp
                      style={{ marginRight: 5, verticalAlign: "middle" }}
                    />
                    WhatsApp
                  </a>
                )}
                {officer.phone && (
                  <a
                    href={`tel:${officer.phone}`}
                    className="contact-btn phone"
                    title="Phone"
                  >
                    <FaPhone
                      style={{ marginRight: 5, verticalAlign: "middle" }}
                    />
                    Phone
                  </a>
                )}
                {officer.email && (
                  <a
                    href={`mailto:${officer.email}`}
                    className="contact-btn email"
                    title="Email"
                  >
                    <FaEnvelope
                      style={{ marginRight: 5, verticalAlign: "middle" }}
                    />
                    Email
                  </a>
                )}
                {officer.facebook && (
                  <a
                    href={officer.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-btn facebook"
                    title="Facebook"
                  >
                    <FaFacebook
                      style={{ marginRight: 5, verticalAlign: "middle" }}
                    />
                    Facebook
                  </a>
                )}
                {officer.instagram && (
                  <a
                    href={officer.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-btn instagram"
                    title="Instagram"
                  >
                    <FaInstagram
                      style={{ marginRight: 5, verticalAlign: "middle" }}
                    />
                    Instagram
                  </a>
                )}
                {officer.tiktok && (
                  <a
                    href={officer.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-btn tiktok"
                    title="TikTok"
                  >
                    <FaTiktok
                      style={{ marginRight: 5, verticalAlign: "middle" }}
                    />
                    TikTok
                  </a>
                )}
              </div>
            </div>

            {/* Approval & Notes Section */}
            <div className="dash-officer-status">
              <div className="officer-notes">
                <strong>
                  {" "}
                  Note from{" "}
                  {officer && officer.name ? `${officer.name}` : "Loan officer"}
                  :
                </strong>
                <div>
                  {(clientInfo && clientInfo.lastNoteToClient) ||
                    "No note from your officer yet."}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p
            style={{
              color: "#666",
              textAlign: "left",
              fontStyle: "italic",
              margin: 0,
            }}
          >
            Awaiting officer assignment.
          </p>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          marginBottom: "0.7em",
        }}
      >
        <div
          className="officer-contact-btns"
          style={{ flexWrap: "wrap", gap: "10px" }}
        >
          {/* ...contact buttons... */}
        </div>

        {/* Calculator */}
        {(purchaseComplete || refinanceComplete) && (
          <>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowCalculator(true);
              }}
              title="Open Mortgage Calculator"
            >
              <img
                src="/assets/Calculator.png"
                alt="Calculator"
                style={{
                  width: "48px",
                  height: "48px",
                  display: "block",
                }}
              />
            </a>
            {showCalculator && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(20,30,40,0.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 6px 40px #2223",
                    padding: "1.9em 2.5em 1.5em 2.5em",
                    minWidth: 330,
                    maxWidth: "94vw",
                    maxHeight: "97vh",
                    overflow: "auto",
                    position: "relative",
                  }}
                >
                  <button
                    onClick={() => setShowCalculator(false)}
                    style={{
                      position: "absolute",
                      right: 18,
                      top: 16,
                      fontSize: "1.7em",
                      background: "none",
                      border: "none",
                      color: "#222",
                      cursor: "pointer",
                    }}
                    aria-label="Close Calculator"
                  >
                    ×
                  </button>
                  <AmortizationCalculator />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* End of calculator code */}

      {(purchaseComplete || refinanceComplete) && (
        <>
          {formData && (
            <>
              {/* Header field with soft background */}
              <div
                style={{
                  backgroundColor: "#e6f9f7",
                  padding: "0.75rem 1.25rem",
                  borderRadius: "8px",
                  marginBottom: "1rem",
                  border: "1px solid #cce7e4",
                  textAlign: "left",
                }}
              >
                <h2 style={{ color: "#0096b2", margin: 0 }}>
                  {selectedView} Information:
                </h2>
              </div>

              {/* Responsive Grid */}
              <div className="dash-summary-grid">
                <div className="dash-card">
                  <strong>First Name:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    {formData?.firstName || "N/A"}
                  </div>
                </div>
                <div className="dash-card">
                  <strong>Last Name:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    {formData?.lastName || "N/A"}
                  </div>
                </div>
                <div className="dash-card">
                  <strong>Email:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    {formData?.email || "N/A"}
                  </div>
                </div>
                <div className="dash-card">
                  <strong>Phone:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    {formatPhone(formData?.phone)}
                  </div>
                </div>
                <div className="dash-card">
                  <strong>Marital Status:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    {formData?.maritalStatus || "N/A"}
                  </div>
                </div>
                <div className="dash-card">
                  <strong>Location:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    {formData?.location || "N/A"}
                  </div>
                </div>
                <div className="dash-card">
                  <strong>Income:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <span>Monthly:&nbsp;</span>
                    {formatCurrency(formData?.income)}
                  </div>
                </div>
                <div className="dash-card">
                  <strong>Employment:</strong>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    {formData?.employmentType || "N/A"}
                  </div>
                </div>
              </div>

              {formData.maritalStatus === "Married" &&
                hasSpouseData(formData) && (
                  <>
                    <h3
                      style={{
                        marginTop: "2rem",
                        color: "#0096b2",
                        borderBottom: "1px solid #ccc",
                        paddingBottom: "0.25rem",
                        marginBottom: "1rem",
                        textAlign: "left",
                      }}
                    >
                      {formData?.spouseFirstName}'s Information
                    </h3>
                    <div className="dash-summary-grid">
                      <div className="dash-card">
                        <strong>Spouse Name:</strong>
                        <div style={{ width: "100%", textAlign: "center" }}>
                          {formData?.spouseFirstName} {formData?.spouseLastName}
                        </div>
                      </div>
                      <div className="dash-card">
                        <strong>Spouse Email:</strong>
                        <div style={{ width: "100%", textAlign: "center" }}>
                          {formData?.spouseEmail || "N/A"}
                        </div>
                      </div>
                      <div className="dash-card">
                        <strong>Spouse Phone:</strong>
                        <div style={{ width: "100%", textAlign: "center" }}>
                          {formatPhone(formData?.spousePhone)}
                        </div>
                      </div>
                      <div className="dash-card">
                        <strong>Spouse Income:</strong>
                        <div style={{ width: "100%", textAlign: "center" }}>
                          <span>Monthly:&nbsp;</span>
                          {formatCurrency(formData?.spouseIncome)}
                        </div>
                      </div>
                      <div className="dash-card">
                        <strong>Spouse Employment:</strong>
                        <div style={{ width: "100%", textAlign: "center" }}>
                          {formData?.spouseEmploymentType || "N/A"}
                        </div>
                      </div>
                    </div>
                  </>
                )}
            </>
          )}

          {clientInfo.formDataPurchase && clientInfo.formDataRefinance && (
            <div className="dash-switch-btn-container">
              <button
                className="dash-switch-btn"
                onClick={() =>
                  setSelectedView(
                    selectedView === "Purchase" ? "Refinance" : "Purchase"
                  )
                }
              >
                {selectedView === "Purchase"
                  ? "Refinance\nInfo"
                  : "Purchase\nInfo"}
              </button>
            </div>
          )}
        </>
      )}

      {/* START / CONTINUE BUTTONS */}
      {!purchaseComplete && (
        <div className="dash-section" style={{ marginBottom: "2.5rem" }}>
          <h3 className="dash-section-header" style={{ fontWeight: 700 }}>
            Purchase
          </h3>
          {purchaseStarted ? (
            <>
              <p>You have not finished your Purchase PreQual:</p>
              <button
                style={circleButtonStyle}
                onClick={() => navigate(`/en/questions?id=${docId}`)}
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <p>Click to start your Purchase PreQual:</p>
              <button
                style={circleButtonStyle}
                onClick={() => navigate(`/en/questions?id=${docId}`)}
              >
                Start
              </button>
            </>
          )}
        </div>
      )}

      {!refinanceComplete && (
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ color: "#0096b2", marginBottom: "0.5rem" }}>
            Refinance
          </h3>
          {refinanceStarted ? (
            <>
              <p style={{ marginBottom: "0.5rem" }}>
                You have not finished your Refinance PreQual:
              </p>
              <button
                onClick={() => navigate(`/en/refinance?id=${docId}`)}
                style={circleButtonStyle}
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <p style={{ marginBottom: "0.5rem" }}>
                Click to start your Refinance PreQual:
              </p>
              <button
                onClick={() => navigate(`/en/refinance?id=${docId}`)}
                style={circleButtonStyle}
              >
                Start
              </button>
            </>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginTop: "2rem",
          flexWrap: "wrap",
        }}
      >
        {/* LEFT SIDE: PDF buttons + Edit */}
        {(purchaseComplete || refinanceComplete) && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {/* PDF download links if available AND approved */}
              {selectedView === "Purchase" &&
                clientInfo.purchasePreQualApproved &&
                pdfLinks.purchase &&
                Object.entries(pdfLinks.purchase).map(([label, url]) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dash-pdf-link"
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "#0096b2",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "6px",
                      fontWeight: "bold",
                      textAlign: "center",
                      minWidth: "160px",
                      marginBottom: "6px",
                      display: "inline-block",
                    }}
                  >
                    {label} PDF
                  </a>
                ))}

              {selectedView === "Refinance" &&
                clientInfo.refinancePreQualApproved &&
                pdfLinks.refinance &&
                Object.entries(pdfLinks.refinance).map(([label, url]) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dash-pdf-link"
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "#0096b2",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "6px",
                      fontWeight: "bold",
                      textAlign: "center",
                      minWidth: "160px",
                      marginBottom: "6px",
                      display: "inline-block",
                    }}
                  >
                    {label} PDF
                  </a>
                ))}

              <button
                onClick={() => {
                  if (
                    window.confirm(
                      "Warning: Continuing will permanently delete your PreQual PDF. You will need to complete the questions again. Are you sure you want to proceed?"
                    )
                  ) {
                    handleEditAnswers();
                  }
                }}
                style={{
                  padding: "10px 16px",
                  backgroundColor: "#0096b2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  minWidth: "160px",
                }}
              >
                Edit Your Answers
              </button>
            </div>
          </>
        )}

        {/* CENTERED LOGOUT */}
        <div
          style={{ width: "100%", textAlign: "center", marginTop: "1.5rem" }}
        >
          <button
            onClick={handleLogout}
            style={{
              padding: "10px 16px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
              minWidth: "160px",
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </main>
  );
}
